import { AppLauncher } from '@capacitor/app-launcher'
import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect } from 'react'
import ReactPlayer from 'react-player'
import styled from 'styled-components'

import { mixpanel } from '@/mixpanel'

import type { PluginListenerHandle } from '@capacitor/core'

type Props = {
  title: string
  videoUrl: string
  posterUrl?: string
  onClose?: () => void
  onComplete?: () => void
  start: boolean
  setStart: (start: boolean) => void
}

const isIOS = () => Capacitor.getPlatform?.() === 'ios'
const isYouTube = (u: string) => /(youtube\.com|youtu\.be)/i.test(u)

function ytWatchUrl(url: string) {
  try {
    const u = new URL(url)
    if (u.hostname.includes('youtu.be')) {
      const id = u.pathname.slice(1)
      if (id) return `https://www.youtube.com/watch?v=${id}&autoplay=1`
    }
    if (u.hostname.includes('youtube.com')) {
      const v = u.searchParams.get('v')
      if (v) return `https://www.youtube.com/watch?v=${v}&autoplay=1`
      const m = u.pathname.match(/\/(shorts|embed)\/([^#/?]+)/)
      if (m) return `https://www.youtube.com/watch?v=${m[2]}&autoplay=1`
    }
  } catch {
    return url
  }
  return url
}

function ytExtractId(url: string): string | null {
  try {
    const u = new URL(url)
    if (u.hostname.includes('youtu.be')) {
      return u.pathname.slice(1) || null
    }
    if (u.hostname.includes('youtube.com')) {
      const v = u.searchParams.get('v')
      if (v) {
        return v
      }
      const m = u.pathname.match(/\/(shorts|embed)\/([^#/?]+)/)
      if (m) return m[2]
    }
  } catch {
    return null
  }
  return null
}

function ytAppUrl(url: string): string | null {
  const id = ytExtractId(url)
  if (!id) return null
  return `youtube://watch?v=${id}`
}

export const VideoPlayer = ({ title, videoUrl, posterUrl, onComplete, start, setStart }: Props) => {
  const useExternalBrowserForPlayback = isIOS() && isYouTube(videoUrl)

  // When using the in-app browser (iOS + YouTube), only trigger completion
  // after the user closes the browser overlay. Calling onComplete immediately
  // after Browser.open can leave the page in a state where the header is not
  // rendered when returning to the app.
  useEffect(() => {
    if (!useExternalBrowserForPlayback) return

    let handle: PluginListenerHandle | undefined
    const setup = async () => {
      handle = await Browser.addListener('browserFinished', () => {
        mixpanel.track('video_browser_closed', { title, url: videoUrl })
        onComplete?.()
      })
    }
    void setup()

    return () => {
      handle?.remove()
    }
  }, [useExternalBrowserForPlayback, title, videoUrl, onComplete])

  useEffect(() => {
    if (start) {
      mixpanel.track('video_play', { title })
    } else {
      mixpanel.track('video_pause', { title })
    }
  }, [start, title])

  if (useExternalBrowserForPlayback) {
    const handleOpen = async () => {
      try {
        const appUrl = ytAppUrl(videoUrl)
        if (appUrl) {
          const can = await AppLauncher.canOpenUrl({ url: appUrl })
          if (can.value) {
            await AppLauncher.openUrl({ url: appUrl })
            mixpanel.track('video_open_in_app', { title, url: videoUrl })
            return
          }
        }
        await Browser.open({
          url: ytWatchUrl(videoUrl),
          presentationStyle: 'popover',
        })
        mixpanel.track('video_open_in_browser', { title, url: videoUrl })
      } catch (error) {
        console.error('Failed to open external video', error)
        mixpanel.track('video_error', { title, url: videoUrl })
      }
    }

    return (
      <PlayerFrame>
        {posterUrl ? <PosterImage src={encodeURI(posterUrl)} alt={title} /> : null}
        <PlayOverlay onClick={handleOpen}>
          <PlayOverlayIcon icon={['fas', 'play']} />
        </PlayOverlay>
      </PlayerFrame>
    )
  }

  return (
    <PlayerFrame>
      {start ? null : (
        <>
          {posterUrl ? <PosterImage src={encodeURI(posterUrl)} alt={title} /> : null}
          <PlayOverlay onClick={() => setStart(true)}>
            <PlayOverlayIcon icon={['fas', 'play']} />
          </PlayOverlay>
        </>
      )}
      <ReactPlayer
        url={videoUrl}
        width='100%'
        height='100%'
        style={{ position: 'absolute', inset: 0 }}
        className='react-player'
        playing={start}
        controls={start}
        config={{
          vimeo: {
            playerOptions: {
              controls: true,
              title: false,
              byline: false,
              portrait: false,
              playsinline: 1,
            },
          },
          youtube: {
            playerVars: {
              fs: 1,
              rel: 0,
              modestbranding: 1,
              playsinline: 1,
              enablejsapi: 1,
              origin: typeof window === 'undefined' ? undefined : window.location.origin,
            },
          },
        }}
        onEnded={() => {
          mixpanel.track('video_complete', { title })
          onComplete?.()
        }}
      />
    </PlayerFrame>
  )
}

const PlayerFrame = styled.div`
  position: relative;
  width: 100%;
  max-width: 720px;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Large}px;
  background-color: black;

  & > .react-player,
  & > .react-player iframe,
  & > .react-player video {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
  }
`

const PosterImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
  pointer-events: none;
`

const PlayOverlay = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 0.375rem;
  background-color: rgba(0, 0, 0, 0.5);
  cursor: pointer;
  z-index: 20;
`

const PlayOverlayIcon = styled(FontAwesomeIcon)`
  width: 1.25rem;
  height: 1.25rem;
  color: ${({ theme }) => theme.Colors.Background.White};
  cursor: pointer;
`
